import { ConditionGroup, mergeAllConditions } from '@hogwarts/conditionals';

import { v4 as uuid } from 'uuid';

export const colors = {
  danger: '#f8d7da',
  warning: '#fff3cd',
  success: '#d4edda',
  primary: '#cce5ff',
  secondary: '#e2e3e5',

  red: 'red',
};

export const organisationTypes = {
  UK: 'UK',
  Brazil: 'Brazil',
  Chile: 'Chile',
  Spain: 'Spain',
  Asia: 'Asia',
  Dubai: 'Dubai',
  Wales: 'Wales',
  CH: 'CH', // swiss
  US: 'US',
};

// Region display labels for UI (extends organisationTypes with display overrides)
export const REGION_DISPLAY_LABELS = {
  ...organisationTypes,
  CH: 'Switzerland (CH)',
  US: 'United States (US)',
};

interface ValueSelector {
  value: string;
  condition?: ConditionGroup;
}
interface FormatSelector {
  condition?: ConditionGroup;
  format: {
    fill?: string;
  };
}
type Selector = ValueSelector | FormatSelector;

interface Token {
  key: string;
  value: string;
  selectors: Selector[];
}

const getNameToken = (key: string, field: string): Token => {
  return {
    key,
    // default/fallback value
    value: `{{${field}}}`,
    selectors: [
      {
        condition: {
          variables: {
            field$: {
              type: 'string',
              source: ['field', field],
            },
          },
          conditions: [
            {
              when: 'field$',
              comparison: 'isNull',
            },
          ],
        },
        value: '(No Name)',
      },
    ],
  };
};

const ewc_expiration_date = '24ab9acbfc3642feaa35b51511f07d91_expiry';
const ewc_expiry = '24ab9acbfc3642feaa35b51511f07d91';
const ewc_checkedby = 'ewc_checkedby';
const ewc_datechecked = 'ewc_datechecked';
const ewc_number = 'ewc_number';

const getDateToken = (key: string, field: string): Token => {
  return {
    key,
    // default/fallback value
    value: `{{${field}:DATE_MED}}`,
    selectors: [
      {
        condition: {
          variables: {
            field$: {
              type: 'date',
              source: ['field', field],
            },
          },
          conditions: [
            {
              when: 'field$',
              comparison: 'isNull',
            },
          ],
        },
        value: '(No Date)',
      },
    ],
  };
};

const columnFactory = (
  props: Partial<{
    required: string;
    complete: string;
    date: string;
    by: string;
    noby: string;
    nodate: string;

    textComplete: string;
    textNotComplete: string;
    textCompleteOverride: any;

    notCompleteCondition: ConditionGroup;
    notCompleteComparison: any;
    textNotRequired: string;

    include?: string[];

    profileTypes: string[];

    // TODO: What are these? (dbs thing)
    emptyby: string;
    emptydate: string;
  }>
): Partial<Column> => {
  let {
    required,
    complete,
    date,
    by,
    noby,
    nodate,

    textComplete,
    textNotComplete,
    textCompleteOverride,

    notCompleteCondition,
    notCompleteComparison,

    textNotRequired,

    include,

    profileTypes,

    // TODO: What are these? (dbs thing)
    emptyby,
    emptydate,
  } = props;

  textComplete = textComplete || 'Satisfactory';
  textNotComplete = textNotComplete || 'Not Completed';
  textNotRequired = textNotRequired || 'Not Applicable';

  const tokens = [
    nodate && getDateToken('nodate', nodate),
    noby && getNameToken('noby', noby),
    by && getNameToken('by', by),
    date && getDateToken('complete_date', date),
  ].filter(Boolean);

  const getNotRequiredCondition = (): ConditionGroup => {
    return {
      variables: {
        [`required$`]: {
          type: 'boolean',
          source: ['field', required],
        },
      },
      conditions: [
        {
          when: `required$`,
          comparison: 'isFalse',
        },
      ],
    };
  };

  const getNotCompleteCondition = (): ConditionGroup => {
    if (notCompleteCondition) {
      return notCompleteCondition;
    }

    if (!complete) {
      throw new Error('Expected Complete Field');
    }

    return {
      variables: {
        [`complete$`]: {
          type: 'boolean',
          source: ['field', complete],
        },
      },
      conditions: [
        {
          when: `complete$`,
          comparison: notCompleteComparison || 'isFalse',
        },
      ],
    };
  };

  const templateSelectors: ValueSelector[] = [];

  if (profileTypes?.length) {
    templateSelectors.push({
      condition: {
        variables: {
          typeKey$: {
            type: 'string',
            // TODO: Need to add this to the mappers
            // Would also be useful to work with the data-mongo-query
            source: ['typeKey'],
          },
        },
        conditions: [
          {
            when: `typeKey$`,
            comparison: [
              'notOneOf',
              {
                values: profileTypes,
              },
            ],
          },
        ],
      },
      value: 'Not Applicable',
    });
  }

  if (required) {
    // see if the required field is true
    // if its not then we show "Not Applicable"
    // And show the noby and nodate

    templateSelectors.push({
      condition: getNotRequiredCondition(),
      value: noby
        ? `${textNotRequired}\n{{noby}}\n{{nodate}}`
        : `${textNotRequired}`,
    });

    // else, move onto the complete field step.
  }

  const formatSelectors: FormatSelector[] = [];

  if (textCompleteOverride) {
    const { value, ...textCompleteOverrideCondition } = textCompleteOverride;
    const notCompleteCondition2 = mergeAllConditions([
      textCompleteOverrideCondition,
      getNotCompleteCondition(),
    ]);
    templateSelectors.push({
      condition: notCompleteCondition2,
      value,
    });
    formatSelectors.push({
      condition: notCompleteCondition2,
      format: {
        fill: colors.red,
      },
    });
  }

  const notCompleteCondition3 = getNotCompleteCondition();
  templateSelectors.push({
    condition: notCompleteCondition3,
    value: `${textNotComplete}`,
  });
  formatSelectors.push({
    condition: notCompleteCondition3,
    format: {
      fill: colors.red,
    },
  });

  const include2 = include?.length
    ? `\n${include.map((field) => `{{${field}}}`).join('\n')}`
    : '';

  templateSelectors.push({
    value: `${textComplete}\n{{complete_date}}\n{{by}}${include2}`,
  });

  return {
    formatSelectors,
    template: {
      tokens,
      selectors: templateSelectors,
    },
  };

  // TemplateSelectors - based on a condition, offer up a template

  // Types of columns
  // Ones where field is required
  // If its required, is it complete or not
  // If its not required, does it have a sign off (noBy, noDate)

  // Global sanctions is a bit more complicated for what is complete and whats not

  // References has 2 lots of fields in it. Only has complete/not complete though
};

const referenceChecksFactory = (): {
  formatSelectors: FormatSelector[];
  template: {
    tokens: Token[];
    selectors: ValueSelector[];
  };
} => {
  const getNotCompleteCondition = (field: string): ConditionGroup => {
    const varname = `${field}$`;
    return {
      variables: {
        [varname]: {
          type: 'boolean',
          source: ['field', field],
        },
      },
      conditions: [
        {
          when: varname,
          comparison: 'isFalse',
        },
      ],
    };
  };

  const tokens = [
    getDateToken('first_date', 'reference_firstdatechecked'),
    getDateToken('second_date', 'reference_seconddatechecked'),
  ].filter(Boolean);

  return {
    formatSelectors: [
      {
        condition: {
          ...mergeAllConditions([
            getNotCompleteCondition('reference_firstcheckcomplete'),
            getNotCompleteCondition('reference_secondcheckcomplete'),
          ]),
          operator: 'or',
        },
        format: {
          fill: colors.red,
        },
      },
    ],
    template: {
      tokens,
      selectors: [
        {
          condition: mergeAllConditions([
            getNotCompleteCondition('reference_firstcheckcomplete'),
            getNotCompleteCondition('reference_secondcheckcomplete'),
          ]),
          value: 'Not Completed',
        },

        {
          condition: getNotCompleteCondition('reference_firstcheckcomplete'),
          value: [
            'Not Completed',
            `Satisfactory\n{{second_date}}\n{{reference_secondcheckedby}}`,
          ].join('\n'),
        },

        {
          condition: getNotCompleteCondition('reference_secondcheckcomplete'),
          value: [
            `Satisfactory\n{{first_date}}\n{{reference_firstcheckedby}}`,
            `Not Completed`,
          ].join('\n'),
        },

        {
          value: [
            `Satisfactory\n{{first_date}}\n{{reference_firstcheckedby}}`,
            `Satisfactory\n{{second_date}}\n{{reference_secondcheckedby}}`,
          ].join('\n'),
        },
      ],
    },
  };
};

const ewcChecksFactory = (): {
  formatSelectors: FormatSelector[];
  template: {
    tokens: Token[];
    selectors: ValueSelector[];
  };
} => {
  // const getEWCNumberToken = (): Token => {
  //   return {
  //     key: ewc_number,
  //     // default/fallback value
  //     value: `{{${ewc_number}}}`,
  //     selectors: [
  //       {
  //         condition: {
  //           variables: {
  //             field$: {
  //               type: 'string',
  //               source: ['field', ewc_number],
  //             },
  //           },
  //           conditions: [
  //             {
  //               when: 'field$',
  //               comparison: 'isNull',
  //             },
  //           ],
  //         },
  //         value: '',
  //       },
  //     ],
  //   };
  // };

  const getNotCompleteCondition = (): ConditionGroup => {
    // EWC is empty
    return {
      variables: {
        [`${ewc_number}$`]: {
          type: 'boolean',
          source: ['field', ewc_number],
        },
        [`${ewc_expiry}$`]: {
          type: 'date',
          source: ['field', ewc_expiry],
        },
      },
      conditions: [
        {
          when: `${ewc_number}$`,
          comparison: 'isNull',
        },
        {
          when: `${ewc_expiry}$`,
          comparison: 'isNull',
        },
      ],
      operator: 'or',
    };
  };

  const getNotApplicableCondition = (): ConditionGroup => {
    // EWC is 'N/A'
    return {
      variables: {
        [`${ewc_number}$`]: {
          type: 'string',
          source: ['field', ewc_number],
        },
      },
      conditions: [
        {
          when: `${ewc_number}$`,
          comparison: [
            'equals',
            {
              value: 'N/A',
            },
          ],
        },
      ],
    };
  };

  const getHasExpired = (): ConditionGroup => {
    return {
      variables: {
        [`expiration_date$`]: {
          type: 'date',
          source: ['field', ewc_expiration_date],
        },
      },
      conditions: [
        {
          when: `expiration_date$`,
          comparison: [
            'isDuring',
            {
              range: 'beforetomorrow',
            },
          ],
        },
      ],
      operator: 'or',
    };
  };

  /*
  If EWC is 'N/A' then show 'Satisfactory'
  If EWC is empty, then show 'Not Completed'

  if EWC has a value, then show 'Satisfactory' and the date and name

  if Expiry date is empty, then show 'Not Completed'
  if Expiry date has value after today, then show 'Satisfactory' and the date and name
  if Expiry date has value before today, then show 'Unsatisfactory' 

*/

  // const tokens = [getEWCNumberToken()].filter(Boolean);

  return {
    formatSelectors: [
      {
        condition: getNotCompleteCondition(),
        format: {
          fill: colors.red,
        },
      },
      {
        condition: getHasExpired(),
        format: {
          fill: colors.red,
        },
      },
    ],
    template: {
      tokens: [],
      selectors: [
        {
          condition: getNotApplicableCondition(),
          value: ['Not Applicable'].join('\n'),
        },
        {
          condition: getNotCompleteCondition(),
          value: ['Not Completed'].join('\n'),
        },
        {
          condition: getHasExpired(),
          value: [
            'EWC Expired',
            `{{${ewc_expiry}:DATE_MED}}`,
            '{{ewc_checkedby}}',
            `No. {{${ewc_number}}}`,
          ].join('\n'),
        },
        {
          value: [
            `Satisfactory`,
            `{{${ewc_datechecked}:DATE_MED}}`,
            '{{ewc_checkedby}}',
          ].join('\n'),
        },
      ],
    },
  };
};

interface Column {
  label: string;
  type?: string;
  types?: string[];
  width?: string;
  visible?: boolean;
  template?:
    | {
        tokens: Token[];
        selectors: Selector[];
      }
    | string;
  formatSelectors?: FormatSelector[];
}

export const cognitaReportFactory = ({ obfuscated, organisationType }) => {
  const x: Column[] = [
    {
      label: obfuscated ? 'Post & Start Date' : 'Name, Post & Start Date',
      width: 'auto',
      type: 'template',
      template: {
        tokens: [
          {
            key: 'startdate2',
            // default/fallback value
            value: `{{startdate:DATE_MED}}`,
            selectors: [
              {
                condition: {
                  variables: {
                    startdate$: {
                      type: 'date',
                      source: ['field', 'startdate'],
                    },
                  },
                  conditions: [
                    {
                      when: 'startdate$',
                      comparison: 'isNull',
                    },
                  ],
                },
                value: '(No Date)',
              },
            ],
          },
        ],
        selectors: [
          {
            value: obfuscated
              ? `{{employment_position}}\n{{startdate2}}`
              : `{{legal_name}}\n{{employment_position}}\n{{startdate2}}`,
          },
        ],
      },
    },
    {
      label: 'ID Check',
      ...columnFactory({
        // dev_test: true, ??
        required: null,
        complete: 'identity_checkcomplete',
        date: 'identity_datechecked',
        by: 'identity_checkedby',
      }),
    },

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'Qualification',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Chile,
          organisationTypes.Spain,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Professional / Academic Qualifications',
      },
    ].map(({ label, types }) => {
      const x: Column = {
        label,
        types,
        ...columnFactory({
          required: 'qualification_checksrequired',
          complete: 'qualification_checkcomplete',
          date: 'qualification_datechecked',
          by: 'qualification_checkedby',
          noby: '5d483029c093850012d62ac1',
          nodate: '5da84dc0ec0bbb00128bf16f',
        }),
      };
      return x;
    }),

    {
      label: 'Local Check', // (DBS Custom Section)
      types: [organisationTypes.Brazil],
      ...columnFactory({
        required: null,
        complete: '5dbaf99aedb78e0012415f9c',
        date: '5dbaf9c1edb78e0012415fda',
        by: '5dbaf9b1edb78e0012415fd6',
      }),
    },

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'DBS Level',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: null,
        complete: 'cognita_dbs_dbschecked',
        date: 'cognita_dbs_datechecked',
        by: 'cognita_dbs_checkedby',

        textNotComplete: 'Pending Risk Assessment on File',
        textComplete: 'Enhanced DBS Certificate',

        include: ['cognita_dbs_disclosurenumber'],
      }),
    })),

    ...[
      {
        types: [organisationTypes.Spain],
        label: 'Local Sex Offenders Check',
      },
      {
        types: [
          organisationTypes.Chile,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Local Criminal/Criminality Check',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: null,
        complete: 'cognita_dbs_dbschecked',
        date: 'cognita_dbs_datechecked',
        by: 'cognita_dbs_checkedby',

        // textNotComplete: 'Pending Risk Assessment on File',
        // textComplete: 'Enhanced DBS Certificate',
        // include: ['cognita_dbs_disclosurenumber'],
      }),
    })),

    {
      label: 'EWC Check',
      types: [organisationTypes.Wales],
      ...ewcChecksFactory(),
    },

    {
      label: 'Barred List',
      types: [organisationTypes.UK, organisationTypes.Wales],
      ...columnFactory({
        required: 'cognita_dbs_regulated',
        complete: 'cognita_dbs_barredlistchecked',
        date: 'cognita_dbs_barredlistdatechecked',
        by: 'cognita_dbs_barredlistcheckedby',

        // textNotComplete: 'EWC not Complete',
        // textNotRequired: 'Not Applicable (Not regulated activity)',
        // noby: '',
        // nodate: '',
      }),
    },
    {
      // agency staff only
      label: 'Enhanced DBS seen by school - Supply only',
      types: [organisationTypes.UK, organisationTypes.Wales],

      ...columnFactory({
        // required: 'cognita_dbs_regulated',
        complete: 'cognita_dbs_copysupplied',
        date: 'cognita_dbs_date_certificate_supplied',
        by: 'cognita_dbs_certificate_supplied_checkedby',
        emptyby: '',
        emptydate: '',
        textNotComplete: 'Not Supplied',
        profileTypes: ['agency'],
        // textNotRequired: 'Not Applicable (Not regulated activity)',
      }),
    },

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'Right to Work in the UK',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Spain,
          organisationTypes.Chile,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Right to Work in Country',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: null,
        complete: 'right_to_work_checkcomplete',
        date: 'right_to_work_datechecked',
        by: 'right_to_work_checkedby',
      }),
    })),

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'Overseas',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Spain,
          organisationTypes.Chile,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Overseas checks (Criminality)',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: 'outside_uk_checksrequired',
        complete: 'outside_uk_checkcomplete',
        date: 'outside_uk_datechecked',
        by: 'outside_uk_checkedby',

        noby: '5d4af3d2c7f1f0001282455c',
        nodate: '5d4af3ebc7f1f0001282455d',
      }),
    })),
    {
      label: 'Prohibition from Teaching',
      types: [organisationTypes.UK, organisationTypes.Spain],
      ...columnFactory({
        required: 'prohibition_checksrequired',
        complete: 'prohibition_checkcomplete',
        date: 'prohibition_datechecked',
        by: 'prohibition_checkedby',

        noby: '5d48345bec57d70012d565ea',
        nodate: '5d48346fc093850012d62ac4',
      }),
    },

    // Wasn't shown in last version.
    // {
    //   label: 'EEA Sanctions and Restrictions',
    //   types: [UK, Spain],
    ///  template: columnFactory({
    //     required: 'outside_uk_eeachecksrequired',
    //     complete: 'outside_uk_eeacheckcomplete',
    //     date: 'outside_uk_eeadatechecked',
    //     by: 'outside_uk_eeacheckedby',

    //     nodate: '5d4aed96250f5300125f9df8',
    //     noby: '5d4aed81250f5300125f9df7',
    //   }),
    // },

    {
      label: 'Prohibition from Management',
      types: [organisationTypes.UK, organisationTypes.Spain],
      ...columnFactory({
        required: 'section_128_checksrequired',
        complete: 'section_128_checkcomplete',
        date: 'section_128_datechecked',
        by: 'section_128_checkedby',

        nodate: '5d483508ec57d70012d565ef',
        noby: '5d4834f5ec57d70012d565ee',
      }),
    },

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'References',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Spain,
          organisationTypes.Chile,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Reference Checks',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...referenceChecksFactory(),
    })),

    ...[
      {
        types: [organisationTypes.Chile],
        label: 'Psychological Checks',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d666efa0af74300120750fa',

        complete: '5d6670710af7430012075100',
        notCompleteComparison: [
          'startsWith',
          {
            value: 'Recommended',
          },
        ],

        textCompleteOverride: {
          variables: {
            [`complete$`]: {
              type: 'boolean',
              source: ['field', '5d6670710af7430012075100'],
            },
          },
          conditions: [
            {
              when: `complete$`,
              comparison: [
                'contains',
                {
                  value: 'observation',
                },
              ],
            },
          ],
          value: 'Satisfactory with Observation',
        },

        date: '5d666ff50af74300120750fe',
        by: '5d66700c0af74300120750ff',

        nodate: '5d666fa70af74300120750fc',
        noby: '5d666f7b0af74300120750fb',
      }),
    })),

    ...[
      {
        types: [organisationTypes.Asia],
        label: 'Civil Litigation',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d83037582962d00120cc1a9',

        complete: '5d5e6d286b9d7d0012e968f8',

        date: '5d5e6d5798230700129b2d07',
        by: '5d5e6d486b9d7d0012e968f9',
      }),
    })),

    ...[
      {
        types: [organisationTypes.Asia],
        label: 'Global Sanctions and Restrictions',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d49440e9023110012c20e82',

        // TODO: what is this?

        notCompleteCondition: {
          variables: {
            [`checkedBy$`]: {
              type: 'boolean',
              source: ['field', '5d4944259023110012c20e83'],
            },
            [`dateChecked$`]: {
              type: 'boolean',
              source: ['field', '5d4944339023110012c20e84'],
            },
          },
          conditions: [
            {
              when: `checkedBy$`,
              comparison: 'isNull',
            },
            {
              when: `dateChecked$`,
              comparison: 'isNull',
            },
          ],
        },

        // completeTest: (data) => {
        //   const checkedBy = data['5d4944259023110012c20e83'];
        //   const dateChecked = data['5d4944339023110012c20e84'];
        //   return !emptyValue(checkedBy) && !emptyValue(dateChecked);
        // },

        date: '5d4944339023110012c20e84',
        by: '5d4944259023110012c20e83',

        nodate: '5d4944509023110012c20e86',
        noby: '5d4944409023110012c20e85',
      }),
    })),

    ...[
      {
        types: [organisationTypes.Asia],
        label: 'Social Media',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d5ac1e8534bbf0012a1e19c',

        complete: '5d3defe7bc9a950012e7b822',

        date: '5d3df005bc9a950012e7b823',
        by: '5d3df016bc9a950012e7b824',
      }),
    })),

    ...[
      {
        types: [organisationTypes.Brazil, organisationTypes.Asia],
        label: 'Financial Checks',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d5e6d8d6b9d7d0012e968fa',

        complete: '5d5e6dcf98230700129b2d08',

        date: '5d5e6e0098230700129b2d0a',
        by: '5d5e6df098230700129b2d09',

        nodate: '5d5e6e29e6610d0012fe524b',
        noby: '5d5e6e156b9d7d0012e968fb',
      }),
    })),

    ...[
      {
        types: [organisationTypes.Chile],
        label: 'Childcare Disqualification',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: 'childcare_checksrequired',

        complete: 'childcare_checkcomplete',

        date: 'childcare_datechecked',
        by: 'childcare_checkedby',
      }),
    })),

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'Application Form',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Spain,
          organisationTypes.Chile,
          organisationTypes.Asia,
          organisationTypes.Dubai,
        ],
        label: 'Employment History',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: '5d56b6d4005a0d0013791671',
        complete: '5d3deeb1775dff00125a0054',
        date: '5d3deec3775dff00125a0055',
        by: '5d3deecd775dff00125a0056',

        nodate: '5d56b76827825100125469c5',
        noby: '5d56b74827825100125469c4',
      }),
    })),

    ...[
      {
        types: [
          organisationTypes.UK,
          organisationTypes.Brazil,
          organisationTypes.Dubai,
        ],
        label: 'Medical',
      },
      {
        types: [organisationTypes.Asia],
        label: 'Medical Declaration',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      ...columnFactory({
        required: null,
        complete: 'medical_checkcomplete',
        date: 'medical_datechecked',
        by: 'medical_checkedby',
      }),
    })),

    ...[
      {
        types: [organisationTypes.UK, organisationTypes.Wales],
        label: 'Last Note Recorded',
      },
      {
        types: [
          organisationTypes.Brazil,
          organisationTypes.Spain,
          organisationTypes.Chile,
          organisationTypes.Dubai,
        ],
        label: 'Notes',
      },
    ].map(({ label, types }) => ({
      label,
      types,
      type: 'template',
      template: '{{notes}}',
    })),
  ];

  return x
    .filter((c) => {
      if (!c.types) {
        return true;
      }

      if (c.types.includes(organisationType)) {
        return true;
      }

      return false;
    })
    .map(({ types, visible, ...rest }) => ({
      ...rest,
      key: uuid().replace('-', ''),
      type: 'template',
      visible: visible !== false,
    }));
};
